import apiClient, { ApiError } from '../api/client';
import type { RunState } from '../api/types';

/**
 * Participant-readable run lifecycle (#242). Returned by
 * `GET /api/public/runs/{run_id}/state` for a caller who is a member of a team
 * in the run — lifecycle fields only, nothing org/coordinator-scoped.
 */
export interface PublicRunState {
  run_id: string;
  name: string;
  state: RunState;
  scheduled_for: string | null;
  started_at: string | null;
  paused_at: string | null;
  ended_at: string | null;
}

export interface PublicRun {
  run_id: string;
  rallye_name: string;
  /** Spec 026 — slug of the organisation owning this run. */
  organization_slug: string;
}

export interface PublicRunMyTeam {
  run_id: string;
  /** Null when the caller has no membership in this run yet. */
  team_id: string | null;
  /** Null while the team is still in the name-finding phase (spec 027). */
  team_name: string | null;
  team_share_code: string | null;
}

const FETCH_TIMEOUT_MS = 3000;

/**
 * Fetch the public, minimal projection of a run by its join code.
 *
 * Returns `null` for any non-success outcome (404, network error, timeout).
 * Spec 025 FR-006 calls for non-blocking behavior on the Welcome screen —
 * callers should render without the rallye name when this resolves to null.
 */
export async function fetchByCode(
  code: string,
  signal?: AbortSignal,
): Promise<PublicRun | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const { data } = await apiClient.get<PublicRun>(
      `/api/public/runs/${encodeURIComponent(code)}`,
      { signal: controller.signal },
    );
    return data;
  } catch {
    return null;
  } finally {
    clearTimeout(timeoutId);
  }
}

export type FetchMyTeamResult =
  | { kind: 'unauthenticated' }
  | { kind: 'not-found' }
  | { kind: 'error' }
  | { kind: 'ok'; data: PublicRunMyTeam };

/**
 * Fetch the authenticated caller's team in the run identified by `code`.
 *
 * Used by the participant onboarding flow to short-circuit FindTeam when
 * the caller is already in a team. The endpoint requires auth — anonymous
 * callers (or callers whose session expired) get `{ kind: 'unauthenticated' }`.
 */
export async function fetchMyTeam(
  code: string,
  signal?: AbortSignal,
): Promise<FetchMyTeamResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }

  try {
    const { data } = await apiClient.get<PublicRunMyTeam>(
      `/api/public/runs/${encodeURIComponent(code)}/my-team`,
      { signal: controller.signal },
    );
    return { kind: 'ok', data };
  } catch (err) {
    // The client interceptor normalises every failed response into an ApiError,
    // so read the status off that — reaching for the raw axios `err.response`
    // silently missed 404 (already wrapped) and would miss 401 too.
    const status = err instanceof ApiError ? err.status : undefined;
    if (status === 401) return { kind: 'unauthenticated' };
    if (status === 404) return { kind: 'not-found' };
    return { kind: 'error' };
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Fetch the participant-readable lifecycle state of a run by its **id** (#242).
 *
 * Authorized by team membership, so a guest gets run-state without the
 * coordinator-scoped `/api/orgs/...` endpoints. Returns `null` for any
 * non-success outcome (401/404/network) — the participant shell falls back to
 * the brand wordmark and the run-state features stay dormant until it resolves.
 */
export async function fetchRunState(
  runId: string,
  signal?: AbortSignal,
): Promise<PublicRunState | null> {
  try {
    const { data } = await apiClient.get<PublicRunState>(
      `/api/public/runs/${encodeURIComponent(runId)}/state`,
      signal ? { signal } : undefined,
    );
    return data;
  } catch {
    return null;
  }
}
